import React from 'react';
import { Signal } from '@preact/signals-react';
import { FaFile, FaFilePdf, FaFileWord, FaFileExcel, FaFileImage, FaTimes } from 'react-icons/fa';

interface ArtifactFilesUploadProps {
    artifactFiles: Signal<File[]>;
    existingFiles: string[];
    removedFiles: string[];
    onRemoveExisting: (fileName: string) => void;
    onRestoreExisting: (fileName: string) => void;
    creditOperationId: string;
    onDownload: (creditOperationId: string, fileName: string) => void;
}

const ArtifactFilesUpload: React.FC<ArtifactFilesUploadProps> = ({
    artifactFiles,
    existingFiles,
    removedFiles,
    onRemoveExisting,
    onRestoreExisting,
    creditOperationId,
    onDownload
}) => {
    const getFileIcon = (fileName: string) => {
        const ext = fileName.split('.').pop()?.toLowerCase();
        switch (ext) {
            case 'pdf': return <FaFilePdf className="text-red-500 text-2xl" />;
            case 'doc':
            case 'docx': return <FaFileWord className="text-blue-500 text-2xl" />;
            case 'xls':
            case 'xlsx': return <FaFileExcel className="text-green-500 text-2xl" />;
            case 'jpg':
            case 'jpeg':
            case 'png':
            case 'gif': return <FaFileImage className="text-purple-500 text-2xl" />;
            default: return <FaFile className="text-gray-500 text-2xl" />;
        }
    };

    return (
        <div>
            <h2 className="text-xl font-semibold mt-4 dark:text-white">Artefatos</h2>
            
            {removedFiles.length > 0 && (
                <div className="mt-2 p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-700 rounded">
                    <p className="text-sm font-medium text-red-700 dark:text-red-300 mb-2">Arquivos removidos:</p>
                    <ul className="text-sm text-red-600 dark:text-red-400 space-y-1">
                        {removedFiles.map((fileName, index) => (
                            <li key={index} className="flex items-center justify-between gap-2">
                                <div className="flex items-center gap-2">
                                    <span className="w-2 h-2 bg-red-400 rounded-full"></span>
                                    {fileName}
                                </div>
                                <button
                                    onClick={() => onRestoreExisting(fileName)}
                                    className="bg-green-500 text-white px-2 py-1 rounded text-xs hover:bg-green-600"
                                >
                                    Restaurar
                                </button>
                            </li>
                        ))}
                    </ul>
                </div>
            )}

            <div className="mt-4">
                <div className="flex items-center justify-center w-full">
                    <label htmlFor="artifacts-upload"
                        className="flex flex-col items-center justify-center w-full h-32 border-2 border-dashed border-gray-300 dark:border-gray-600 rounded-lg cursor-pointer bg-gray-50 dark:bg-gray-800 hover:bg-gray-100 dark:hover:bg-gray-700"
                    >
                        <div className="flex flex-col items-center justify-center pt-5 pb-6">
                            <svg className="w-8 h-8 mb-4 text-gray-500 dark:text-gray-400" aria-hidden="true" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 20 16">
                                <path stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 13h3a3 3 0 0 0 0-6h-.025A5.56 5.56 0 0 0 16 6.5 5.5 5.5 0 0 0 5.207 5.021C5.137 5.017 5.071 5 5 5a4 4 0 0 0 0 8h2.167M10 15V6m0 0L8 8m2-2 2 2" />
                            </svg>
                            <p className="mb-2 text-sm text-gray-500 dark:text-gray-400">
                                <span className="font-semibold">Clique aqui para subir arquivos</span> ou arraste e solte aqui.
                            </p>
                        </div>
                        <input
                            id="artifacts-upload"
                            type="file"
                            className="hidden"
                            multiple
                            onChange={(e) => {
                                const files = Array.from(e.target.files || []);
                                artifactFiles.value = [...artifactFiles.value, ...files];
                            }}
                        />
                    </label>
                </div>
            </div>

            {existingFiles.filter(file => !removedFiles.includes(file)).length > 0 && (
                <div className="mt-2 p-3 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-700 rounded">
                    <p className="text-sm font-medium text-green-700 dark:text-green-300 mb-2">Arquivos atuais:</p>
                    <ul className="text-sm text-green-600 dark:text-green-400 space-y-1">
                        {existingFiles.filter(file => !removedFiles.includes(file)).map((fileName, index) => (
                            <li key={index} className="flex items-center justify-between gap-2">
                                <div className="flex items-center gap-2">
                                    <span className="w-2 h-2 bg-green-400 rounded-full"></span>
                                    <button
                                        onClick={() => onDownload(creditOperationId, fileName)}
                                        className="underline hover:text-green-800 dark:hover:text-green-200 cursor-pointer bg-transparent border-0 p-0"
                                    >
                                        {fileName}
                                    </button>
                                </div>
                                <button
                                    onClick={() => onRemoveExisting(fileName)}
                                    className="bg-red-500 text-white px-2 py-1 rounded text-xs hover:bg-red-600"
                                >
                                    <FaTimes className="inline" />
                                </button>
                            </li>
                        ))}
                    </ul>
                </div>
            )}

            {artifactFiles.value.length > 0 && (
                <div className="mt-4 p-4 border rounded bg-gray-50 dark:bg-gray-800 dark:border-gray-700">
                    <h3 className="text-sm font-medium mb-3 dark:text-gray-300">Arquivos Selecionados ({artifactFiles.value.length})</h3>
                    <div className="flex flex-wrap gap-4">
                        {artifactFiles.value.map((file, index) => (
                            <div key={index} className="relative flex flex-col items-center p-3 bg-white dark:bg-gray-700 border dark:border-gray-600 rounded shadow-sm hover:shadow-md transition-shadow">
                                <button
                                    onClick={() => artifactFiles.value = artifactFiles.value.filter((_, i) => i !== index)}
                                    className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full w-5 h-5 flex items-center justify-center text-xs hover:bg-red-600"
                                >
                                    <FaTimes />
                                </button>
                                {getFileIcon(file.name)}
                                <span className="text-xs mt-2 text-center max-w-20 truncate dark:text-gray-300" title={file.name}>
                                    {file.name}
                                </span>
                            </div>
                        ))}
                    </div>
                </div>
            )}
        </div>
    );
};

export default ArtifactFilesUpload;
